import { BookOpen, ChevronDown, SaveIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { KeyboardEvent, useCallback, useState } from "react";

import ConfirmSnippetChanges from "./ConfirmSnippetChanges";
import useKeyboardShortcut from "./hooks/useKeyboardShortcut";
import useSaveTemplate from "./hooks/useSaveTemplate";
import useTemplateForm from "./hooks/useTemplateForm";
import useTemplateSubmit from "./hooks/useTemplateSubmit";
import useTemplateValidation from "./hooks/useTemplateValidation";
import ParsingErrorConfirmation from "./ParsingErrorConfirmation";

import { usePlayground, useSnippetOverrideStore } from "@/components/Playground/playground-store";
import { PlaygroundDialog } from "@/components/Playground/PlaygroundDialog";
import DeploymentGuideDialog from "@/components/PromptTemplate/DeploymentGuideDialog";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { TOUR_TARGET_IDS } from "@/components/Playground/tour/constants";
import TourDisabledArea from "@/components/Playground/tour/TourDisabledArea";
import TourTargetContainer from "@/components/Playground/tour/TourTargetContainer";
import SelectTemplate from "@/components/SelectTemplate";
import { Button, ButtonSize } from "@/components/ui/button";
import CommitMessageDialog from "@/components/ui/CommitMessageDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuBasicTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup } from "@/components/ui/radio-group";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { SHORTCUTS } from "@/utils/shortcuts";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";

const PROMPT_RADIO_ITEMS = [
  {
    label: "Update existing template",
    value: "update",
    id: "update",
  },
  {
    label: "Create new template",
    value: "create",
    id: "create",
  },
];

const SNIPPET_RADIO_ITEMS = [
  {
    label: "Update existing snippet",
    value: "update",
    id: "update",
  },
  {
    label: "Create new snippet",
    value: "create",
    id: "create",
  },
];

const SaveTemplateDialog = ({
  disabled = false,
  size = "sm",
}: {
  disabled?: boolean;
  size?: ButtonSize;
}) => {
  const [open, setOpen] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [showParsingErrorDialog, setShowParsingErrorDialog] = useState(false);
  const [showSnippetChangesDialog, setShowSnippetChangesDialog] = useState(false);
  const [showDeploymentGuide, setShowDeploymentGuide] = useState(false);
  const playground = usePlayground();
  const snippetOverrideStore = useSnippetOverrideStore();
  const { handleCreate, handleUpdate, isCreating, isUpdating } = useSaveTemplate();
  const { isEditDisabled, isCreateDisabled } = useRbac();
  const { isSnippet } = playground;
  const entityType = isSnippet ? EntityType.SNIPPET : EntityType.PROMPT;
  const saveDisabled = isEditDisabled(entityType) && isCreateDisabled(entityType);
  const tour = useOptionalPlaygroundTourContext();

  const {
    saveType,
    setSaveType,
    selectedTemplate,
    setSelectedTemplate,
    selectedTemplateId,
    setSelectedTemplateId,
    newTemplateName,
    setNewTemplateName,
  } = useTemplateForm();

  const isValid = useTemplateValidation(
    saveType,
    newTemplateName,
    selectedTemplateId,
    isCreating,
    isUpdating,
  );

  const [submittedVersion, submit] = useTemplateSubmit(
    saveType,
    newTemplateName,
    selectedTemplateId,
    handleCreate,
    handleUpdate,
    isValid,
    setOpen,
  );

  const hasSnippetOverrides = useCallback(
    () =>
      snippetOverrideStore?.snippetOverrides &&
      Object.keys(snippetOverrideStore.snippetOverrides).length > 0,
    [snippetOverrideStore?.snippetOverrides],
  );

  const handleNext = async () => {
    if (playground.hasParsingErrors) {
      setOpen(false);
      setShowParsingErrorDialog(true);
      return;
    }

    if (hasSnippetOverrides()) {
      setOpen(false);
      setShowSnippetChangesDialog(true);
      return;
    }

    if (saveType === "update") {
      setOpen(false);
      setShowCommitDialog(true);
    } else {
      submit(undefined, undefined, true);
    }
  };

  const handleParsingErrorConfirm = () => {
    setShowParsingErrorDialog(false);

    if (hasSnippetOverrides()) {
      setShowSnippetChangesDialog(true);
      return;
    }

    if (saveType === "update") {
      setShowCommitDialog(true);
    } else {
      submit(undefined, undefined, true);
    }
  };

  const handleParsingErrorCancel = () => {
    setShowParsingErrorDialog(false);
    setOpen(true);
  };

  const handleSnippetChangesConfirm = () => {
    setShowSnippetChangesDialog(false);
    if (saveType === "update") {
      setShowCommitDialog(true);
    } else {
      submit(undefined, undefined, true);
    }
  };

  const handleSnippetChangesCancel = () => {
    setShowSnippetChangesDialog(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValid()) {
      e.preventDefault();
      handleNext();
    }
  };

  const handleButtonClick = useCallback(() => {
    if (playground.isEditor && saveType === "update") {
      if (playground.hasParsingErrors) {
        setShowParsingErrorDialog(true);
        return;
      }

      if (hasSnippetOverrides()) {
        setShowSnippetChangesDialog(true);
      } else {
        setShowCommitDialog(true);
      }
    } else {
      setOpen(true);
    }
  }, [playground.isEditor, playground.hasParsingErrors, saveType, hasSnippetOverrides]);

  useKeyboardShortcut(() => !disabled && handleButtonClick());

  return (
    <>
      <PlaygroundDialog handleButtonClick={handleButtonClick} />
      <Dialog open={open} onOpenChange={setOpen}>
        <TourTargetContainer
          id={TOUR_TARGET_IDS.SAVE_TEMPLATE}
          as="span"
          className="relative isolate inline-flex max-w-full"
        >
          <BasicTooltip
            content={
              saveDisabled
                ? `You don't have permission to save this ${isSnippet ? "snippet" : "prompt"}`
                : (playground.isEditor
                    ? "Save changes as a new version"
                    : `Save ${isSnippet ? "snippet" : "template"} to your registry`) +
                  ` (${SHORTCUTS.SAVE.displayWithPlus})`
            }
          >
            <Button
              size={size}
              className="h-8 min-w-0 gap-1.5 rounded-r-none px-2.5 text-xs font-medium shadow-sm transition-all duration-200 hover:brightness-105 active:scale-[0.98]"
              disabled={saveDisabled || disabled}
              onClick={e => {
                e.preventDefault();
                handleButtonClick();
              }}
            >
              <span className="relative flex shrink-0 items-center">
                <SaveIcon className="h-3.5 w-3.5 opacity-75" />
                {playground.hasBeenEdited && (
                  <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-300 shadow-sm" />
                )}
              </span>
              <span className="whitespace-nowrap leading-none">
                {isSnippet ? "Save Snippet" : "Save Template"}
              </span>
              <kbd className="ml-0.5 select-none rounded border border-white/20 bg-white/10 px-1 py-px font-mono text-[10px] font-medium leading-none opacity-65 transition-opacity duration-200 ease-in-out">
                {SHORTCUTS.SAVE.display}
              </kbd>
            </Button>
          </BasicTooltip>
          <DropdownMenu>
            <BasicTooltip content="More Template Actions">
              <DropdownMenuBasicTrigger asChild disabled={disabled}>
                <Button
                  size={size}
                  className="h-8 w-8 shrink-0 rounded-l-none border-l border-white/20 p-0 shadow-sm"
                  aria-label="More Template Actions"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuBasicTrigger>
            </BasicTooltip>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                className="!flex !w-full min-w-0 gap-2 overflow-hidden"
                onSelect={() => setShowDeploymentGuide(true)}
              >
                <BookOpen className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                <OverflowDisplayTooltip
                  content="View Deployment Guide"
                  className="min-w-0 flex-1 truncate text-left"
                >
                  View Deployment Guide
                </OverflowDisplayTooltip>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {tour?.isActive && <TourDisabledArea className="z-30" />}
        </TourTargetContainer>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{isSnippet ? "Save Snippet" : "Save Prompt Template"}</DialogTitle>
            <DialogDescription>
              {isSnippet
                ? "Create a new snippet or update an existing one"
                : "Create a new template or update an existing one"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <RadioGroup
              value={saveType}
              onValueChange={(value: "update" | "create") => setSaveType(value)}
              items={isSnippet ? SNIPPET_RADIO_ITEMS : PROMPT_RADIO_ITEMS}
            />

            <hr />

            {saveType === "update" && (
              <div className="grid gap-2">
                <Label>
                  {isSnippet ? "Select snippet to update:" : "Select template to update:"}
                </Label>
                <SelectTemplate
                  template={
                    selectedTemplate && selectedTemplateId
                      ? { name: selectedTemplate, id: selectedTemplateId }
                      : undefined
                  }
                  onSelect={({ id, name }) => {
                    setSelectedTemplate(name);
                    setSelectedTemplateId(typeof id === "number" ? id : Number(id));
                  }}
                  isSnippet={isSnippet}
                />
              </div>
            )}

            {saveType === "create" && (
              <div className="grid gap-2">
                <Label>{isSnippet ? "New snippet name:" : "New template name:"}</Label>
                <Input
                  placeholder="Enter name..."
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleNext} disabled={!isValid() || disabled}>
              {isCreating || isUpdating ? "Saving..." : "Next"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeploymentGuideDialog
        open={showDeploymentGuide}
        onOpenChange={setShowDeploymentGuide}
        promptName={selectedTemplate || newTemplateName || undefined}
        inputVariables={playground.detectedInputVariables}
      />
      <CommitMessageDialog
        isLoading={isCreating || isUpdating}
        onConfirm={commitMessage => submit(commitMessage, undefined, true)}
        onGoBack={() => {
          setShowCommitDialog(false);
          setOpen(true);
        }}
        submittedVersion={submittedVersion || undefined}
        isOpen={showCommitDialog}
        data={playground.blueprint}
        prompt={{
          name: selectedTemplate,
          id: selectedTemplateId,
        }}
        onClose={() => setShowCommitDialog(false)}
        title={isSnippet ? "Save New Snippet Version" : undefined}
        saveTitle={isSnippet ? "Snippet Saved" : undefined}
        commitLabel={isSnippet ? "Snippet version change message (Optional)" : undefined}
      />
      <ParsingErrorConfirmation
        isOpen={showParsingErrorDialog}
        onConfirm={handleParsingErrorConfirm}
        onCancel={handleParsingErrorCancel}
        saveType={saveType}
        templateName={saveType === "create" ? newTemplateName : selectedTemplate}
      />
      <ConfirmSnippetChanges
        isOpen={showSnippetChangesDialog}
        onConfirm={handleSnippetChangesConfirm}
        onCancel={handleSnippetChangesCancel}
      />
    </>
  );
};

export default observer(SaveTemplateDialog);
